import { wrapDispatch, CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { EditReturn, ListReturn, Status, convertError } from 'lib/rpc'
import { Collector, unaryListCollect } from 'lib/rpcts'
import {
    AddAppOwnerRequest,
    AppOwnerRef,
    ListAppOwnersRequest,
    ListAppOwnersResponse,
    RemoveAppOwnerRequest,
} from 'pbts/c1/api/app/v1/app_owner'
import { AppOwnersClient, AppOwnersSearchClient } from 'pbts/c1/api/app/v1/app_owner.client'
import { User } from 'pbts/c1/api/user/v1/user'
import { Dispatch } from 'react'
import { createMutationApi } from 'redux_toolkit/lib/createMutationApi'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'

export enum AppOwnersAction {
    list = 'AppOwnersAction.list',
    edit = 'AppOwnersAction.edit',
}

export type OwnersListAction = {
    type: AppOwnersAction.list
    payload: ListReturn<User>
}

export type OwnersEditAction = {
    type: AppOwnersAction.edit
    payload: EditReturn
}

export class AppOwnerCollector extends Collector<User> {
    push(resp: ListAppOwnersResponse): void {
        this.buf = this.buf.concat(resp.list)
    }
}

export type AppOwnersActions = OwnersListAction | OwnersEditAction

export const listAppOwners = (
    dispatch: Dispatch<AppOwnersActions>,
    appId: string,
    client?: AppOwnersClient,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const collector = new AppOwnerCollector()
    return unaryListCollect<ListAppOwnersRequest, ListAppOwnersResponse>(
        ListAppOwnersRequest.create({
            appId,
        }),
        client.list.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppOwnersAction.list,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                        },
                    })
                    break
                default:
                    dispatch({
                        type: AppOwnersAction.list,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const editAppOwners = (
    dispatcher: Dispatch<AppOwnersActions>,
    client: AppOwnersClient | undefined,
    appId: string,
    toAdd: User[],
    toRemove: User[],
): (() => void) | undefined => {
    const [dispatch, cancel] = wrapDispatch(dispatcher)
    if (!client) {
        return
    }

    let cancelled = false
    dispatch({ type: AppOwnersAction.edit, payload: { status: Status.LOADING } })

    const doEditAppOwners = async (): Promise<void> => {
        try {
            for (let i = 0; i < toAdd.length && !cancelled; i++) {
                const req = AddAppOwnerRequest.create({ appId, userId: toAdd[i].id })

                await client.add(req)
            }

            for (let i = 0; i < toRemove.length && !cancelled; i++) {
                const req = RemoveAppOwnerRequest.create({ appId, userId: toRemove[i].id })

                await client.remove(req)
            }
        } catch (err) {
            dispatch({ type: AppOwnersAction.edit, payload: { status: Status.ERROR, error: convertError(err) } })
            return
        }
        dispatch({ type: AppOwnersAction.edit, payload: { status: Status.OK } })
    }

    doEditAppOwners()

    return (): void => {
        cancel()
        cancelled = true
    }
}

export const listAppOwnersApi = createPagedListApi(
    AppOwnersClient,
    'list',
    undefined,
    AppOwnerRef.typeName,
)

export const searchAppOwnersApi = createPagedListApi(
    AppOwnersSearchClient,
    'search',
    undefined,
    AppOwnerRef.typeName,
)

export const setAppOwnersApi = createMutationApi(
    AppOwnersClient,
    'set',
    undefined,
    AppOwnerRef.typeName,
)

export const addAppOwnersApi = createMutationApi(
    AppOwnersClient,
    'add',
    undefined,
    AppOwnerRef.typeName,
)
